import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ALERTS } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import { BellRing, CheckCircle2, Trash2, ShieldAlert, WifiOff, Ban, Bug, ShieldCheck } from "lucide-react";

const typeIcon = {
  captcha: ShieldAlert,
  ban: Ban,
  shadowban: Ban,
  device_offline: WifiOff,
  task_failed: Bug,
  warning: BellRing,
};

const severityStyles = {
  low: "bg-slate-100 text-slate-700 ring-slate-200",
  medium: "bg-amber-100 text-amber-700 ring-amber-200",
  high: "bg-rose-100 text-rose-700 ring-rose-200",
  critical: "bg-rose-600 text-white ring-rose-600",
};

export default function Alerts() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setList(await api.alerts()); }
    catch { toast.error(t.common.failed); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const ack = async (id) => {
    try { await api.ackAlert(id); load(); }
    catch { toast.error(t.common.failed); }
  };
  const del = async (id) => {
    try { await api.deleteAlert(id); load(); }
    catch { toast.error(t.common.failed); }
  };
  const resolveCaptcha = async (id) => {
    try {
      await api.resolveCaptcha(id);
      toast.success(t.alerts.captchaResolved);
      load();
    } catch { toast.error(t.common.failed); }
  };

  return (
    <div data-testid={ALERTS.root}>
      <PageHeader title={t.alerts.title} description={t.alerts.description} />
      {loading ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : list.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <BellRing className="h-10 w-10 text-emerald-300 mx-auto" />
          <div className="font-display text-lg mt-3 text-slate-900">{t.alerts.empty}</div>
          <div className="text-sm text-slate-500 mt-1">{t.alerts.emptyHint}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((a) => {
            const Icon = typeIcon[a.type] || BellRing;
            return (
              <div key={a.id} className={`card-soft p-5 ${a.acknowledged ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
                    a.severity === "critical" || a.severity === "high"
                      ? "bg-rose-50 text-rose-600"
                      : a.severity === "medium"
                      ? "bg-amber-50 text-amber-600"
                      : "bg-slate-100 text-slate-600"
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-semibold text-slate-900">{a.title}</div>
                      <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ring-1 ring-inset ${severityStyles[a.severity]}`}>
                        {t.alerts.severity[a.severity]}
                      </span>
                      <span className="text-[10px] uppercase tracking-widest text-slate-500">
                        {t.alerts.type[a.type] || a.type}
                      </span>
                      {a.acknowledged && (
                        <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                          {t.alerts.acknowledged}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{a.detail}</p>
                    <div className="text-[11px] text-slate-400 mt-2 font-mono">{new Date(a.ts).toLocaleString("ru-RU")}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {a.type === "captcha" && !a.acknowledged && (
                      <button
                        data-testid={ALERTS.resolveBtn(a.id)}
                        onClick={() => resolveCaptcha(a.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium transition-colors"
                      >
                        <ShieldCheck className="h-3.5 w-3.5" /> {t.alerts.resolveCaptcha}
                      </button>
                    )}
                    {!a.acknowledged && a.type !== "captcha" && (
                      <button
                        data-testid={ALERTS.ackBtn(a.id)}
                        onClick={() => ack(a.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-medium transition-colors"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" /> {t.alerts.ack}
                      </button>
                    )}
                    <button
                      onClick={() => del(a.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-rose-100 hover:text-rose-700 text-slate-700 text-xs font-medium transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
